/**
 * ThemeEngine — Enterprise Design System
 *
 * Top-level theme provider that wraps the application with MUI ThemeProvider.
 * Supports:
 * - Light/Dark/System mode
 * - Preset themes (color palettes)
 * - Custom user-defined color overrides
 * - Font size scaling for accessibility
 * - Persists to both localStorage and user profile (when authenticated)
 *
 * On mode change, updates localStorage and re-renders without page reload.
 */

import React, { useMemo, useEffect, useState } from 'react';
import { createTheme, ThemeProvider, lighten, darken } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useDesignSystemStore } from '../store/useDesignSystemStore';
import lightPalette from './lightPalette';
import darkPalette from './darkPalette';
import typography from './typography';
import componentOverrides from './componentOverrides';
import mobileOverrides from './mobileOverrides';
import { SHADOW, BP_MOBILE, BP_LAPTOP, BP_DESKTOP } from './tokens';
import PRESETS from './presets';

/** Detect system preference for dark mode */
function getSystemMode() {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Build a palette by overlaying preset/custom colors onto the base palette */
function buildPalette(resolvedMode, preset, customTheme) {
  const basePalette = resolvedMode === 'dark' ? { ...darkPalette } : { ...lightPalette };

  // Apply preset colors.
  // IMPORTANT: also derive light/dark shades from the preset's main color so
  // every surface that reads palette.primary.light / .dark (table header strip,
  // sidebar accent, gradients, hover states) actually reflects the chosen theme.
  // Previously only `.main` was overridden, so all presets looked nearly identical.
  const presetDef = PRESETS[preset];
  if (presetDef && preset !== 'default') {
    const presetColors = resolvedMode === 'dark' ? presetDef.dark : presetDef.light;
    if (presetColors.primaryColor) {
      basePalette.primary = {
        ...basePalette.primary,
        main: presetColors.primaryColor,
        light: lighten(presetColors.primaryColor, 0.3),
        dark: darken(presetColors.primaryColor, 0.3),
      };
    }
    if (presetColors.secondaryColor) {
      basePalette.secondary = {
        ...basePalette.secondary,
        main: presetColors.secondaryColor,
        light: lighten(presetColors.secondaryColor, 0.3),
        dark: darken(presetColors.secondaryColor, 0.3),
      };
    }
    // Apply the preset accent to the info palette (drives the 3rd gradient stop
    // in table header strips and other accent surfaces).
    if (presetColors.accentColor) {
      basePalette.info = {
        ...basePalette.info,
        main: presetColors.accentColor,
        light: lighten(presetColors.accentColor, 0.3),
        dark: darken(presetColors.accentColor, 0.3),
      };
    }
  }

  // Preset-specific dark backgrounds
  if (resolvedMode === 'dark') {
    if (preset === 'midnightBlue') {
      basePalette.background = { default: '#0a1628', paper: '#0f1f3a' };
      basePalette.divider = '#1e3a5f';
    } else if (preset === 'executiveBlack') {
      basePalette.background = { default: '#0a0a0a', paper: '#141414' };
      basePalette.divider = '#2a2a2a';
    } else if (preset === 'enterpriseDark') {
      basePalette.background = { default: '#111827', paper: '#1f2937' };
      basePalette.divider = '#374151';
    }
  }

  // Apply custom overrides (take priority over preset)
  if (customTheme) {
    if (customTheme.primaryColor) {
      basePalette.primary = {
        ...basePalette.primary,
        main: customTheme.primaryColor,
        light: lighten(customTheme.primaryColor, 0.3),
        dark: darken(customTheme.primaryColor, 0.3),
      };
    }
    if (customTheme.secondaryColor) {
      basePalette.secondary = {
        ...basePalette.secondary,
        main: customTheme.secondaryColor,
        light: lighten(customTheme.secondaryColor, 0.3),
        dark: darken(customTheme.secondaryColor, 0.3),
      };
    }
    if (customTheme.accentColor) {
      basePalette.info = {
        ...basePalette.info,
        main: customTheme.accentColor,
        light: lighten(customTheme.accentColor, 0.3),
        dark: darken(customTheme.accentColor, 0.3),
      };
    }
  }

  return basePalette;
}

/** Font size multiplier */
const FONT_SCALE = { small: 0.875, medium: 1, large: 1.125 };

/**
 * ThemeEngine component.
 * @param {{ children: React.ReactNode }} props
 */
export function ThemeEngine({ children }) {
  const mode = useDesignSystemStore((state) => state.mode);
  const preset = useDesignSystemStore((state) => state.preset);
  const customTheme = useDesignSystemStore((state) => state.customTheme);
  const fontSize = useDesignSystemStore((state) => state.fontSize);

  // Listen to system preference changes when mode is 'system'
  const [systemMode, setSystemMode] = useState(getSystemMode);

  useEffect(() => {
    if (mode !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemMode(e.matches ? 'dark' : 'light');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [mode]);

  const resolvedMode = mode === 'system' ? systemMode : mode;

  const theme = useMemo(() => {
    const palette = buildPalette(resolvedMode, preset, customTheme);
    const scale = FONT_SCALE[fontSize] || 1;

    // Scale typography
    const scaledTypography = { ...typography };
    if (scale !== 1) {
      scaledTypography.fontSize = 14 * scale;
    }

    const borderRadius = customTheme?.borderRadius ?? 10;

    return createTheme({
      breakpoints: {
        values: {
          xs: 0,
          sm: BP_MOBILE,
          md: BP_LAPTOP,
          lg: BP_DESKTOP,
          xl: 1536,
        },
      },
      palette,
      typography: scaledTypography,
      shadows: SHADOW,
      shape: { borderRadius },
      components: { ...componentOverrides, ...mobileOverrides },
    });
  }, [resolvedMode, preset, customTheme, fontSize]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

export default ThemeEngine;
